import { useState, useCallback } from 'react';

const STORAGE_PREFIX = 'guidance-dismissed-';

/**
 * Hook to manage the dismissed state of guidance banners.
 *
 * Two dismiss modes:
 * - Session dismiss (X button): hides banner for current session only
 * - Permanent dismiss (checkbox + X): persists to localStorage, never shows again
 *
 * @param tipId - Unique identifier for the guidance tip
 * @returns Object with dismissed state, dismiss functions, and permanent preference
 */
export function useGuidanceDismissed(tipId: string) {
  const key = `${STORAGE_PREFIX}${tipId}`;

  // Check localStorage for permanent dismissal
  const [permanentlyDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  });

  // Session-only dismissal state
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // "Don't show again" checkbox state
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isDismissed = permanentlyDismissed || sessionDismissed;

  const dismiss = useCallback(() => {
    if (dontShowAgain) {
      // Permanent dismiss — persist to localStorage
      try {
        localStorage.setItem(key, 'true');
      } catch {
        // Ignore localStorage errors
      }
    }
    // Always dismiss for this session
    setSessionDismissed(true);
  }, [key, dontShowAgain]);

  // Auto-dismiss for session only (no checkbox needed)
  // Call this on first meaningful user interaction to clear the banner
  const dismissForSession = useCallback(() => {
    setSessionDismissed(true);
  }, []);

  return {
    isDismissed,
    dismiss,
    dismissForSession,
    dontShowAgain,
    setDontShowAgain,
  };
}

/**
 * Reset all guidance dismissed states.
 * Useful for testing or allowing users to see tips again.
 */
export function resetAllGuidanceDismissed(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore localStorage errors
  }
}
